import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "../lib/api";
import { fmtEUR, fmtDate } from "../lib/format";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import {
  Copy, CheckCircle2, XCircle, Clock3, RefreshCcw, StickyNote, Keyboard,
} from "lucide-react";
import { TESTIDS } from "../lib/testids";
import { useHotkeys } from "../lib/useHotkeys";
import { useNavigate } from "react-router-dom";
import ImpactBanner from "../components/app/ImpactBanner";
import {
  Card, ListRow, StatusPill, NeutralTag, Button, SectionLabel, Meta, Kbd,
} from "../components/app/theme";

/* ── Map tag client → libellé lisible (casse normale) ─────────── */
const CLIENT_TAG_LABEL = {
  A_RISQUE: "Client à risque",
  DORMANT: "Client dormant",
  EN_BAISSE: "Panier en baisse",
};

const RISK_TO_LEVEL = {
  HAUT: "HAUT",
  MOYEN: "MOYEN",
  FAIBLE: "FAIBLE",
};

/* ────────────────────────────────────────────────────────────── */
/* LIST ROW (colonne 1)                                           */
/* ────────────────────────────────────────────────────────────── */
function BriefingRow({ action, active, onClick, index }) {
  const risk = action.niveau_risque;
  const isDormant = action.type === "client";
  const kind = isDormant ? "Client" : action.type === "signal" ? "Signal" : "Propale";

  return (
    <ListRow
      active={active}
      onClick={onClick}
      data-testid={`briefing-list-row-${index}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <StatusPill level={RISK_TO_LEVEL[risk] || "NEUTRAL"} />
        <span className="th-meta" style={{ textTransform: "none" }}>{kind}</span>
        {action.tag && (
          <NeutralTag>{CLIENT_TAG_LABEL[action.tag] || action.tag}</NeutralTag>
        )}
      </div>
      <div
        className="text-[15px] leading-snug truncate"
        style={{
          fontFamily: "var(--th-font-serif)",
          fontWeight: 500,
          color: "var(--th-text)",
        }}
      >
        {action.titre}
      </div>
      <div
        className="text-[13px] leading-snug truncate mt-1"
        style={{ color: "var(--th-text-2)" }}
      >
        {action.justification}
      </div>
    </ListRow>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* CONTEXT PANEL (colonne 3)                                      */
/* ────────────────────────────────────────────────────────────── */
function ContextPanel({ action }) {
  const [client, setClient] = useState(null);

  useEffect(() => {
    if (!action) { setClient(null); return; }
    const nomClient = action.type === "client" ? action.client?.nom : action.propale?.client;
    if (!nomClient) return;
    api.get("/clients").then((r) => {
      const c = r.data.clients.find((cc) => cc.nom === nomClient);
      setClient(c || null);
    }).catch(() => {});
  }, [action]);

  if (!action) return null;
  const propaleClient = action.type === "client" ? action.client?.nom : action.propale?.client;

  return (
    <div className="p-5 space-y-5 text-sm">
      <div>
        <SectionLabel className="mb-2">Contexte client</SectionLabel>
        <div
          className="text-[18px] leading-tight truncate"
          style={{
            fontFamily: "var(--th-font-serif)",
            fontWeight: 500,
            color: "var(--th-text)",
          }}
          title={propaleClient}
        >
          {propaleClient}
        </div>
        {client && (
          <Meta className="mt-1 truncate">
            {client.contact_nom} · {client.contact_email}
          </Meta>
        )}
      </div>

      {client && (
        <>
          <div>
            <SectionLabel className="mb-2">Cycle</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-3">
                <div className="th-meta mb-1">Cycle habituel</div>
                <div
                  className="th-num"
                  style={{
                    fontFamily: "var(--th-font-serif)",
                    fontSize: 18,
                    fontWeight: 500,
                    color: "var(--th-text)",
                  }}
                >
                  {client.anomalie?.cycle_jours || "—"} j
                </div>
              </Card>
              <Card
                className="p-3"
                style={
                  client.anomalie?.en_anomalie
                    ? { borderColor: "var(--th-haut-text)", backgroundColor: "var(--th-haut-bg)" }
                    : undefined
                }
              >
                <div className="th-meta mb-1">Silence actuel</div>
                <div
                  className="th-num"
                  style={{
                    fontFamily: "var(--th-font-serif)",
                    fontSize: 18,
                    fontWeight: 500,
                    color: client.anomalie?.en_anomalie ? "var(--th-haut-text)" : "var(--th-text)",
                  }}
                >
                  {client.anomalie?.silence_jours ?? "—"} j
                </div>
              </Card>
            </div>
            <Meta className="mt-2">
              CA annuel moyen :{" "}
              <span className="th-num" style={{ color: "var(--th-text)" }}>
                {fmtEUR(client.ca_annuel_moyen)}
              </span>
            </Meta>
          </div>

          <div>
            <SectionLabel className="mb-2">Historique commandes (24 mois)</SectionLabel>
            <Card className="max-h-52 overflow-y-auto">
              {(client.historique_commandes || []).slice().reverse().map((h) => (
                <div
                  key={`hist-${h.date}-${h.montant}`}
                  className="flex justify-between px-3 py-2 text-xs"
                  style={{ borderBottom: "1px solid var(--th-border)" }}
                >
                  <span className="th-num" style={{ color: "var(--th-text-3)" }}>{fmtDate(h.date)}</span>
                  <span className="th-num" style={{ color: "var(--th-text)" }}>{fmtEUR(h.montant)}</span>
                </div>
              ))}
              {(!client.historique_commandes || client.historique_commandes.length === 0) && (
                <div className="px-3 py-3 text-xs" style={{ color: "var(--th-text-3)" }}>
                  Aucune commande enregistrée.
                </div>
              )}
            </Card>
          </div>

          {client.propales_en_cours?.length > 0 && (
            <div>
              <SectionLabel className="mb-2">Propales en cours</SectionLabel>
              <Card>
                {client.propales_en_cours.map((p) => (
                  <div
                    key={p.id}
                    className="flex justify-between px-3 py-2 text-xs gap-3"
                    style={{ borderBottom: "1px solid var(--th-border)" }}
                  >
                    <span className="th-num truncate" style={{ color: "var(--th-text-3)" }}>{p.numero}</span>
                    <span className="truncate" style={{ color: "var(--th-text-2)" }}>{p.statut}</span>
                    <span className="th-num" style={{ color: "var(--th-text)" }}>{fmtEUR(p.montant_ht)}</span>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* ACTION DETAIL (colonne 2)                                      */
/* ────────────────────────────────────────────────────────────── */
function ActionDetail({ action, onFeedback, onNote, noteRef }) {
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState(action?.propale?.notes_locales || "");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportDate, setReportDate] = useState(new Date(Date.now() + 3 * 86400000));

  useEffect(() => {
    setNote(action?.propale?.notes_locales || "");
  }, [action?.id]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(action.message);
      setCopied(true);
      toast.success("Message copié");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  useEffect(() => {
    if (!action) return;
    action._copy = copy;
    action._openReport = () => setReportOpen(true);
  });

  if (!action) return (
    <div className="p-12 text-center text-sm" style={{ color: "var(--th-text-3)" }}>
      Sélectionnez une action à gauche pour voir le détail.
    </div>
  );

  const risk = action.niveau_risque;
  const isDormant = action.type === "client";
  const kindLabel = isDormant
    ? (action.tag === "A_RISQUE" ? "Client à risque" : "Client dormant")
    : action.type === "signal" ? "Signal" : "Propale";

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="briefing-detail-panel">
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <StatusPill level={RISK_TO_LEVEL[risk] || "NEUTRAL"} />
          <span className="th-meta" style={{ textTransform: "none" }}>{kindLabel}</span>
          {action.tag && (
            <NeutralTag>{CLIENT_TAG_LABEL[action.tag] || action.tag}</NeutralTag>
          )}
        </div>
        <h2
          className="leading-tight"
          style={{
            fontFamily: "var(--th-font-serif)",
            fontWeight: 500,
            fontSize: 27,
            color: "var(--th-text)",
            letterSpacing: "-0.005em",
          }}
        >
          {action.titre}
        </h2>
        {action.sous_titre && (
          <div className="text-sm mt-1.5" style={{ color: "var(--th-text-2)" }}>
            {action.sous_titre}
          </div>
        )}
      </header>

      <p
        className="text-sm mb-6 pl-3"
        style={{
          color: "var(--th-text-2)",
          borderLeft: "2px solid var(--th-border)",
        }}
      >
        {action.justification}
      </p>

      <Card className="p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>
            Brouillon — modèle « {action.template_key} »
          </SectionLabel>
        </div>
        <pre
          className="text-sm whitespace-pre-wrap leading-relaxed"
          style={{
            fontFamily: "var(--th-font-sans)",
            color: "var(--th-text)",
          }}
        >
{action.message}
        </pre>
        <div className="mt-4">
          <Button
            data-testid={TESTIDS.copyMessageBtn}
            onClick={copy}
            variant="primary"
          >
            {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
            <span>{copied ? "Copié" : "Copier"}</span>
            <Kbd>C</Kbd>
          </Button>
        </div>
      </Card>

      {!isDormant && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <StickyNote size={12} style={{ color: "var(--th-text-3)" }} />
            <SectionLabel>Note personnelle</SectionLabel>
            <Kbd>N</Kbd>
          </div>
          <Textarea
            ref={noteRef}
            data-testid="propale-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => note !== (action.propale?.notes_locales || "") && onNote(action.id, note)}
            placeholder="Ex : il attend le budget T3, décideur en vacances jusqu'au…"
            className="text-sm min-h-[70px]"
            style={{
              backgroundColor: "var(--th-surface)",
              border: "1px solid var(--th-border-strong)",
              borderRadius: 8,
              color: "var(--th-text)",
            }}
            rows={2}
          />
        </div>
      )}

      {!isDormant && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Button
            data-testid={TESTIDS.actionRelance}
            onClick={() => onFeedback(action, "relance")}
            variant="secondary"
            className="h-11 flex-col gap-1"
          >
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} /> Relancé</span>
            <Kbd>R</Kbd>
          </Button>
          <Button
            data-testid={TESTIDS.actionReporter}
            onClick={() => setReportOpen(true)}
            variant="secondary"
            className="h-11 flex-col gap-1"
          >
            <span className="flex items-center gap-1.5"><Clock3 size={14} /> Reporter</span>
            <Kbd>L</Kbd>
          </Button>
          <Button
            data-testid={TESTIDS.actionSigne}
            onClick={() => onFeedback(action, "signe")}
            variant="secondary"
            className="h-11 flex-col gap-1"
          >
            <span className="flex items-center gap-1.5">
              <CheckCircle2 size={14} style={{ color: "var(--th-faible-text)" }} /> Signé
            </span>
            <Kbd>S</Kbd>
          </Button>
          <Button
            data-testid={TESTIDS.actionPerdu}
            onClick={() => onFeedback(action, "perdu")}
            variant="secondary"
            className="h-11 flex-col gap-1"
          >
            <span className="flex items-center gap-1.5">
              <XCircle size={14} style={{ color: "var(--th-haut-text)" }} /> Perdu
            </span>
            <Kbd>P</Kbd>
          </Button>
        </div>
      )}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent style={{ borderRadius: 12 }}>
          <DialogHeader>
            <DialogTitle
              style={{
                fontFamily: "var(--th-font-serif)",
                fontWeight: 500,
                color: "var(--th-text)",
              }}
            >
              Reporter la relance
            </DialogTitle>
            <DialogDescription style={{ color: "var(--th-text-2)" }}>
              Choisissez la date à laquelle cette action réapparaîtra dans le briefing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={reportDate}
              onSelect={(d) => d && setReportDate(d)}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button
              variant="primary"
              onClick={() => {
                const iso = reportDate.toISOString().slice(0, 10);
                onFeedback(action, "reporte", iso);
                setReportOpen(false);
              }}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* PAGE                                                           */
/* ────────────────────────────────────────────────────────────── */
export default function Briefing() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const noteRef = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/briefing");
      setBriefing(r.data);
      setSelectedIdx(0);
    } catch {
      toast.error("Impossible de charger le briefing");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actions = briefing?.actions || [];
  const selected = actions[selectedIdx] || null;

  const applyFeedback = async (action, status, reporte_jusquau = null) => {
    if (action.type === "client") { toast.info("Feedback client à venir"); return; }
    try {
      const r = await api.post(`/propales/${action.id}/status`, {
        local_status: status,
        reporte_jusquau,
      });
      const snapshot = r.data.snapshot;
      const label = status === "relance" ? "Relancé"
        : status === "reporte" ? `Reporté au ${reporte_jusquau}`
        : status === "signe" ? "Signé"
        : "Perdu";

      toast.success(label, {
        duration: 10000,
        action: {
          label: "Annuler",
          onClick: async () => {
            try {
              await api.post(`/propales/${action.id}/revert`, snapshot);
              toast.info("Action annulée");
              load();
            } catch { toast.error("Impossible d'annuler"); }
          },
        },
      });
      const r2 = await api.get("/briefing");
      setBriefing(r2.data);
      setSelectedIdx(0);
    } catch { toast.error("Erreur"); }
  };

  const saveNote = async (id, text) => {
    try {
      await api.patch(`/propales/${id}/note`, { notes_locales: text });
      toast.success("Note enregistrée", { duration: 1500 });
    } catch { toast.error("Impossible d'enregistrer la note"); }
  };

  useHotkeys({
    "ArrowDown": () => setSelectedIdx((i) => Math.min(actions.length - 1, i + 1)),
    "ArrowUp":   () => setSelectedIdx((i) => Math.max(0, i - 1)),
    "j":         () => setSelectedIdx((i) => Math.min(actions.length - 1, i + 1)),
    "k":         () => setSelectedIdx((i) => Math.max(0, i - 1)),
    "r":         () => selected?.type !== "client" && selected && applyFeedback(selected, "relance"),
    "s":         () => selected?.type !== "client" && selected && applyFeedback(selected, "signe"),
    "p":         () => selected?.type !== "client" && selected && applyFeedback(selected, "perdu"),
    "l":         () => selected?.type !== "client" && selected?._openReport && selected._openReport(),
    "c":         () => selected?._copy && selected._copy(),
    "n":         () => noteRef.current?.focus(),
    "Escape":    () => {},
  }, actions.length > 0);

  const dataEmpty = !loading && actions.length === 0;

  return (
    <div className="th-scope h-screen flex flex-col" style={{ backgroundColor: "var(--th-bg)" }}>
      {/* En-tête */}
      <header
        className="px-6 py-4 flex items-center justify-between gap-4"
        style={{
          backgroundColor: "var(--th-bg)",
          borderBottom: "1px solid var(--th-border)",
        }}
      >
        <div className="min-w-0 flex-1">
          <SectionLabel>
            Aujourd&apos;hui · {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </SectionLabel>
          <h1
            className="mt-1 leading-tight"
            style={{
              fontFamily: "var(--th-font-serif)",
              fontWeight: 500,
              fontSize: 27,
              letterSpacing: "-0.005em",
              color: "var(--th-text)",
            }}
          >
            Briefing du jour
          </h1>
          {briefing?.impact && (
            <div className="mt-3 max-w-lg">
              <ImpactBanner impact={briefing.impact} size="compact" />
            </div>
          )}
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs shrink-0" style={{ color: "var(--th-text-3)" }}>
          <span className="flex items-center gap-2">
            <Keyboard size={12} /> Raccourcis :
            <Kbd>↑↓</Kbd>
            <Kbd>R</Kbd>
            <Kbd>S</Kbd>
            <Kbd>P</Kbd>
            <Kbd>L</Kbd>
            <Kbd>C</Kbd>
            <Kbd>N</Kbd>
          </span>
          <Button onClick={load} variant="secondary" className="h-9">
            <RefreshCcw size={12} /> Actualiser
          </Button>
        </div>
      </header>

      {/* Corps */}
      {dataEmpty && (
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="p-10 text-center max-w-md">
            <h2
              className="mb-2"
              style={{
                fontFamily: "var(--th-font-serif)",
                fontWeight: 500,
                fontSize: 22,
                color: "var(--th-text)",
              }}
            >
              Aucune donnée à analyser
            </h2>
            <p className="text-sm mb-6" style={{ color: "var(--th-text-2)" }}>
              Importez vos propales commerciales envoyées et vos clients pour lancer l&apos;analyse.
            </p>
            <Button onClick={() => nav("/reglages")} variant="primary">
              Aller à l&apos;import CSV
            </Button>
          </Card>
        </div>
      )}

      {loading && (
        <div className="p-8 text-sm" style={{ color: "var(--th-text-3)" }}>
          Calcul du briefing…
        </div>
      )}

      {!loading && !dataEmpty && (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[340px_1fr] lg:grid-cols-[340px_1fr_360px]">
          {/* Colonne 1 : liste priorisée */}
          <aside
            className="overflow-y-auto"
            style={{
              backgroundColor: "var(--th-surface)",
              borderRight: "1px solid var(--th-border)",
            }}
            data-testid="briefing-list"
          >
            <div
              className="px-5 py-3 sticky top-0"
              style={{
                backgroundColor: "var(--th-surface)",
                borderBottom: "1px solid var(--th-border)",
              }}
            >
              <SectionLabel>Priorités du jour</SectionLabel>
              <Meta className="mt-1" style={{ textTransform: "none" }}>
                {actions.length} action{actions.length > 1 ? "s" : ""} ·{" "}
                {briefing?.total_propales_actives ?? 0} propales actives ·{" "}
                {briefing?.total_clients_anomalie ?? 0} anomalies
              </Meta>
            </div>
            {actions.map((a) => (
              <BriefingRow
                key={`${a.type}-${a.id}`}
                action={a}
                active={actions[selectedIdx] === a}
                onClick={() => setSelectedIdx(actions.indexOf(a))}
                index={actions.indexOf(a)}
              />
            ))}
          </aside>

          {/* Colonne 2 : détail action */}
          <section
            className="overflow-y-auto"
            style={{ backgroundColor: "var(--th-bg)" }}
          >
            <ActionDetail
              action={selected}
              onFeedback={applyFeedback}
              onNote={saveNote}
              noteRef={noteRef}
            />
          </section>

          {/* Colonne 3 : contexte client */}
          <aside
            className="hidden lg:block overflow-y-auto"
            style={{
              backgroundColor: "var(--th-surface)",
              borderLeft: "1px solid var(--th-border)",
            }}
          >
            <ContextPanel action={selected} />
          </aside>
        </div>
      )}
    </div>
  );
}
